import { useEffect, useRef, useState } from "react";
import { Circle, Grid2x2, Play, Pause, Volume2, ChevronLeft, ChevronRight } from "lucide-react";
import { ttsAudioUrl } from "@/lib/apiClient";
import { resolveAudioSrc } from "@/lib/offlineCache";
import { useI18n } from "@/contexts/I18nContext";
import { renderVerseText, newWordsInAyah } from "../recordingRender";

/**
 * Reading & Listening for module 9 — mirrors LearnReadingListening.jsx's
 * Individual/Group sub-toggle, scoped to the section (ayah or admin-defined
 * ayah group) selected in the shared picker above. Individual steps through
 * that section's ayahs one at a time; Group shows every member ayah
 * together, flowing, exactly as they'll be read in one continuous take
 * during Test. No word-level hover-to-preview — audio only plays via the
 * explicit ▶ Ayah / ▶ New Words controls.
 */
export default function LearnReadingListeningRecording({ module: m, hasSections, selectedSection, sections, onSelectSection }) {
  const { t } = useI18n();
  const [subMode, setSubMode] = useState("individual"); // "individual" | "group"
  const [localIdx, setLocalIdx] = useState(0);
  const [tajweed, setTajweed] = useState(() => localStorage.getItem("qf:tajweed") !== "0");
  const [madaniSize, setMadaniSize] = useState(() => {
    const s = parseInt(localStorage.getItem("qf:m9-size") || "2", 10);
    return Number.isFinite(s) ? Math.max(0, Math.min(3, s)) : 2;
  });
  // Only one of Ayah / New Words can play at a time — the other button
  // hides entirely (not just disables) while its sibling is active.
  const [activePlayback, setActivePlayback] = useState(null); // null | "ayah" | "newWords"
  const [paused, setPaused] = useState(false);
  const audioRef = useRef(null);
  // Holds the current playlist (list + how to resolve each item's audio
  // src) so the shared `ended` listener below can advance to the next
  // item regardless of which button started playback.
  const playlistRef = useRef({ list: [], srcFor: null });
  const playbackIndexRef = useRef(0);

  useEffect(() => { localStorage.setItem("qf:tajweed", tajweed ? "1" : "0"); }, [tajweed]);
  useEffect(() => { localStorage.setItem("qf:m9-size", String(madaniSize)); }, [madaniSize]);
  useEffect(() => {
    setLocalIdx(0);
    setActivePlayback(null);
    setPaused(false);
    audioRef.current?.pause();
  }, [selectedSection, subMode]);

  // Loads and plays the item at `index` in the current playlist; on
  // completion (native `ended` event, wired up below) advances to the
  // next one automatically. Reaching the end of the list clears playback
  // state so the button returns to its idle "Play" appearance.
  const loadAndPlay = (index) => {
    const { list, srcFor } = playlistRef.current;
    const audio = audioRef.current;
    if (index >= list.length || !audio) {
      setActivePlayback(null);
      setPaused(false);
      return;
    }
    resolveAudioSrc(srcFor(list[index])).then((src) => {
      if (!audioRef.current) return;
      audioRef.current.src = src;
      audioRef.current.play().catch(() => {
        // Couldn't play this item (e.g. missing audio) — skip to the next.
        playbackIndexRef.current += 1;
        loadAndPlay(playbackIndexRef.current);
      });
    });
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;
    const onEnded = () => {
      playbackIndexRef.current += 1;
      loadAndPlay(playbackIndexRef.current);
    };
    audio.addEventListener("ended", onEnded);
    return () => audio.removeEventListener("ended", onEnded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allAyahs = (m.passages || []).flatMap((p) => (p.ayahs || []).map((a) => ({ ...a, passage: p })));
  const sectionAyahs = hasSections
    ? allAyahs.filter((a) => (a.rule || String(a.n)) === selectedSection)
    : allAyahs;
  if (sectionAyahs.length === 0) return null;

  const visibleAyahs = subMode === "individual" ? [sectionAyahs[localIdx % sectionAyahs.length]] : sectionAyahs;
  const firstVisible = visibleAyahs[0];
  const passage = firstVisible.passage;
  const isFirstAyahOfPassage = passage.ayahs?.[0]?.n === firstVisible.n;
  const sizeClass = ["size-sm", "size-md", "size-lg", "size-xl"][madaniSize];

  const allNewWords = [...new Set(visibleAyahs.flatMap((a) => newWordsInAyah(a)))];
  // The New Words button plays each visible ayah's admin-uploaded New Words
  // Audio (a separate upload from the ayah's own narrated audio) — never a
  // per-word TTS fallback, so an ayah without one is simply skipped from
  // the playlist rather than substituted.
  const newWordsAyahs = visibleAyahs.filter((a) => a.new_words_audio_url);

  // Edge nav: page within the current section's ayahs when it holds more
  // than one (an admin-defined group), otherwise fall back to moving to the
  // previous/next section entirely — since most sections are a single ayah
  // by default, this is what makes the buttons actually move ayah-to-ayah
  // for real users instead of only working once an admin curates a group.
  const canPageWithinGroup = sectionAyahs.length > 1;
  const canPageAcrossSections = !canPageWithinGroup && Array.isArray(sections) && sections.length > 1 && typeof onSelectSection === "function";
  const sectionIdx = canPageAcrossSections ? sections.indexOf(selectedSection) : -1;
  const showEdgeNav = canPageWithinGroup || canPageAcrossSections;
  const goPrevAyah = () => {
    if (canPageWithinGroup) { setLocalIdx((i) => (i - 1 + sectionAyahs.length) % sectionAyahs.length); return; }
    if (canPageAcrossSections) onSelectSection(sections[(sectionIdx - 1 + sections.length) % sections.length]);
  };
  const goNextAyah = () => {
    if (canPageWithinGroup) { setLocalIdx((i) => (i + 1) % sectionAyahs.length); return; }
    if (canPageAcrossSections) onSelectSection(sections[(sectionIdx + 1) % sections.length]);
  };
  const edgeNavLabel = canPageWithinGroup
    ? `${t("rec.ayah_n", { n: firstVisible.n })} · ${localIdx + 1}/${sectionAyahs.length}`
    : `${t("rec.ayah_n", { n: firstVisible.n })} · ${sectionIdx + 1}/${sections?.length || 0}`;

  const playing = activePlayback === "ayah" && !paused;
  const playingNewWords = activePlayback === "newWords" && !paused;

  // Switching from one playlist to the other (Ayah <-> New Words) must not
  // wait for the current clip to finish or be paused first — stop it
  // outright (pause + rewind) before the other side takes over the shared
  // <audio> element, so there's no gap where the old clip could still fire
  // an `ended` event against the just-replaced playlist/index refs.
  const stopCurrentPlayback = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setPaused(false);
  };

  // Play/Pause toggle: starts a fresh playlist from the top when idle,
  // pauses the currently-loaded clip in place when playing, and resumes
  // from that exact position (native <audio> keeps `currentTime` across
  // pause/play as long as `src` isn't reassigned) when paused. Switching in
  // from the *other* playlist stops it first (see stopCurrentPlayback)
  // instead of refusing to start until it finishes.
  const toggleAyahs = () => {
    if (activePlayback === "ayah") {
      if (paused) { audioRef.current?.play().catch(() => {}); setPaused(false); }
      else { audioRef.current?.pause(); setPaused(true); }
      return;
    }
    if (!audioRef.current) return;
    if (activePlayback === "newWords") stopCurrentPlayback();
    playlistRef.current = {
      list: visibleAyahs,
      srcFor: (a) => (a.narrated_audio_url ? `${process.env.REACT_APP_BACKEND_URL}${a.narrated_audio_url}` : ttsAudioUrl(a.ar)),
    };
    playbackIndexRef.current = 0;
    setActivePlayback("ayah");
    setPaused(false);
    loadAndPlay(0);
  };

  const toggleNewWords = () => {
    if (activePlayback === "newWords") {
      if (paused) { audioRef.current?.play().catch(() => {}); setPaused(false); }
      else { audioRef.current?.pause(); setPaused(true); }
      return;
    }
    if (!audioRef.current || newWordsAyahs.length === 0) return;
    if (activePlayback === "ayah") stopCurrentPlayback();
    playlistRef.current = {
      list: newWordsAyahs,
      srcFor: (a) => `${process.env.REACT_APP_BACKEND_URL}${a.new_words_audio_url}`,
    };
    playbackIndexRef.current = 0;
    setActivePlayback("newWords");
    setPaused(false);
    loadAndPlay(0);
  };

  return (
    <div className="rounded-3xl bg-[#064E3B] text-[#FDFBF7] p-7 sm:p-9 relative overflow-hidden">
      <div className="absolute inset-0 pattern-islamic opacity-25" />
      <div className="relative">
        <div className="sticky top-5 z-30 -mx-4 sm:-mx-6 px-4 sm:px-6 pt-2 pb-2 bg-[#064E3B]/95 backdrop-blur" data-testid="sticky-toolbar">
          {subMode === "individual" && showEdgeNav && (
            <div className="my-2 flex items-center justify-between gap-2 rounded-2xl bg-white/10 border border-white/15 px-3 py-2" data-testid="individual-ayah-nav">
              <button data-testid="individual-ayah-prev" onClick={goPrevAyah}
                aria-label="Previous ayah"
                className="w-10 h-10 shrink-0 rounded-full bg-white/10 hover:bg-white/20 border border-[#FED7AA]/40 grid place-items-center text-[#FED7AA] hover:text-white transition">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#FED7AA]/80">{edgeNavLabel}</span>
              <button data-testid="individual-ayah-next" onClick={goNextAyah}
                aria-label="Next ayah"
                className="w-10 h-10 shrink-0 rounded-full bg-white/10 hover:bg-white/20 border border-[#FED7AA]/40 grid place-items-center text-[#FED7AA] hover:text-white transition">
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          )}
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-white/10 border border-white/15 p-3">
            <div className="inline-flex items-center rounded-full border border-[#FED7AA]/40 overflow-hidden" data-testid="reading-listening-sub-toggle">
              <button
                data-testid="sub-mode-individual"
                onClick={() => setSubMode("individual")}
                className={`inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium transition ${
                  subMode === "individual" ? "bg-[#10B981] text-white" : "text-[#FED7AA] hover:bg-white/10"
                }`}>
                <Circle className="w-4 h-4" /> {t("rd.sub_individual")}
              </button>
              <button
                data-testid="sub-mode-group"
                onClick={() => setSubMode("group")}
                disabled={sectionAyahs.length < 2}
                title={sectionAyahs.length < 2 ? "This ayah isn't part of an admin-defined group" : undefined}
                className={`inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium transition disabled:opacity-30 disabled:cursor-not-allowed ${
                  subMode === "group" ? "bg-[#10B981] text-white" : "text-[#FED7AA] hover:bg-white/10"
                }`}>
                <Grid2x2 className="w-4 h-4" /> {t("rd.sub_group")}
              </button>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button data-testid="new-words-btn" onClick={toggleNewWords} disabled={newWordsAyahs.length === 0}
                title={newWordsAyahs.length === 0 ? t("rec.new_words_unavailable") : t("rec.new_words_hint")}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition border ${
                  activePlayback === "newWords" ? "bg-[#10B981] text-white border-[#10B981]" : "bg-white/10 text-[#FED7AA] border-[#FED7AA]/40 hover:bg-white/20"
                } disabled:opacity-40 disabled:cursor-not-allowed`}>
                {playingNewWords ? <Pause className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
                {t("rec.play_new_words")}
              </button>
              <button data-testid="passage-learn-btn" onClick={toggleAyahs}
                title={t("rec.learn_title")}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition border ${
                  activePlayback === "ayah" ? "bg-[#10B981] text-white border-[#10B981]" : "bg-white/10 text-[#FED7AA] border-[#FED7AA]/40 hover:bg-white/20"
                } disabled:opacity-40 disabled:cursor-not-allowed`}>
                {playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                {t("rec.play_ayah")}
              </button>
              <button
                data-testid="tajweed-toggle"
                onClick={() => setTajweed((v) => !v)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition border ${
                  tajweed ? "bg-white/10 text-[#FED7AA] border-[#FED7AA]" : "bg-transparent text-[#FED7AA]/60 border-[#FED7AA]/40 hover:bg-white/10"
                }`}>
                {t("rec.tajweed")} {tajweed ? t("rec.on") : t("rec.off")}
              </button>
              <div className="inline-flex items-center rounded-full border border-[#FED7AA]/40 overflow-hidden" data-testid="m9-size-control">
                <button onClick={() => setMadaniSize((s) => Math.max(0, s - 1))} disabled={madaniSize === 0}
                  className="px-2.5 py-1.5 text-xs text-[#FED7AA] hover:bg-white/10 transition disabled:opacity-30" aria-label="Smaller">A−</button>
                <span className="px-1.5 text-[10px] text-[#FED7AA]/70 tabular-nums">{t("rd.font_size_label")}</span>
                <button onClick={() => setMadaniSize((s) => Math.min(3, s + 1))} disabled={madaniSize === 3}
                  className="px-2.5 py-1.5 text-xs text-[#FED7AA] hover:bg-white/10 transition disabled:opacity-30" aria-label="Larger">A+</button>
              </div>
            </div>
          </div>
        </div>

        {isFirstAyahOfPassage && passage.prelude && (
          <div className={`font-madani ${sizeClass === "size-xl" ? "size-md" : "size-sm"} text-right mt-4 text-[#FED7AA]/90`}>
            {passage.prelude}
          </div>
        )}

        <div className="mt-6 space-y-4" data-testid="passage-ayahs">
          {visibleAyahs.map((a) => (
            <div key={a.n} className="rounded-2xl bg-white/5 border border-white/10 p-3 sm:p-4">
              {subMode === "group" && (
                <div className="text-[10px] font-mono text-[#FED7AA]/60 mb-1.5" dir="ltr">{t("rec.ayah_n", { n: a.n })}</div>
              )}
              <div className={`font-madani ${sizeClass} text-right`} dir="rtl">
                {renderVerseText(a.ar, newWordsInAyah(a), tajweed)}
              </div>
            </div>
          ))}
        </div>
        <audio ref={audioRef} preload="none" />

        {allNewWords.length > 0 && !tajweed && (
          <div className="mt-5 pt-4 border-t border-[#FED7AA]/20 flex flex-wrap items-center gap-3 text-[11px] text-[#FED7AA]/85" data-testid="new-words-legend">
            <span className="inline-flex items-center gap-1.5">
              <span className="qf-red-word px-1.5 py-0.5 rounded">●</span>
              <span>{t("rec.new_words_hint")}</span>
            </span>
          </div>
        )}
        {tajweed && (
          <div className="mt-4 flex flex-wrap items-center gap-3 text-[10px] text-[#FED7AA]/80 pt-3 border-t border-[#FED7AA]/20" data-testid="tajweed-legend">
            <span><span style={{ color: "#FCD34D" }} className="font-bold">●</span> {t("rec.legend_shaddah")}</span>
            <span><span style={{ color: "#86EFAC" }} className="font-bold">●</span> {t("rec.legend_sukoon")}</span>
            <span><span style={{ color: "#93C5FD" }} className="font-bold">●</span> {t("rec.legend_pause")}</span>
          </div>
        )}
      </div>
    </div>
  );
}
